import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { blank, fallback, OptionalValue, spf, str_split } from '../../../../utils/common';
import { BehaviorSubject } from 'rxjs';
import { FieldmagicDropdownOption } from '../dropdown/dropdown.component';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { toNumber } from 'lodash-es';

@Component({
  selector: 'fieldmagic-interval-input',
  template: `
    <div
      *ngIf="! standalone"
      class="form-group"
    >
      <div class="d-flex d-flex-gap w-100">
        <fieldmagic-text
          *ngIf="label | filled"
          purpose="input"
          [content]="label"
          [withRequiredMarker]="withRequiredMarker"
        >
        </fieldmagic-text>

        <fieldmagic-icon
          *ngIf="tooltip | filled"
          icon="info-circle"
          [tooltip]="tooltip"
        >
        </fieldmagic-icon>
      </div>

      <ng-container *ngTemplateOutlet="input"></ng-container>
    </div>

    <ng-container
      *ngIf="standalone"
      [ngTemplateOutlet]="input"
    >
    </ng-container>

    <ng-template #input>
      <div class="d-flex d-flex-gap w-100">
        <input
          [disabled]="(disabled$ | async) || unit == 'always'"
          type="number"
          [(ngModel)]="size"
          class="w-50 form-control fieldmagic-interval-size-input"
          (ngModelChange)="onInputsChanged()"
          [class.fieldmagic-input-has-error]="errors | filled"
          (blur)="onInputsTouched()"
          placeholder="Enter frequency (e.g., 5)"
        />

        <ng-select
          [disabled]="disabled$ | async"
          [items]="units$ | async"
          [(ngModel)]="unit"
          (ngModelChange)="onInputsChanged()"
          class="w-50 fieldmagic-interval-unit-input"
          [class.fieldmagic-input-has-error]="errors | filled"
          (blur)="onInputsTouched()"
          placeholder="Choose time unit"
          [clearable]="clearable"
        >
          <ng-template
            ng-option-tmp
            let-item="item"
          >
            {{ item | translate }}
          </ng-template>

          <ng-template
            ng-label-tmp
            let-item="item"
          >
            {{ item | translate }}
          </ng-template>
        </ng-select>
      </div>

      <fieldmagic-input-errors
        *ngIf="errors | filled"
        [errors]="errors"
      ></fieldmagic-input-errors>
    </ng-template>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IntervalInputComponent),
      multi: true,
    },
  ],
  styles: [
    `.fieldmagic-interval-size-input {
      font-size: 12px;
      height: 40px;
    }`,
    `.fieldmagic-interval-unit-input ::ng-deep .ng-select-container {
      min-height: 40px !important;
      font-size: 12px;
    }`,
  ]
})
export class IntervalInputComponent implements OnInit, ControlValueAccessor  {
  size?: OptionalValue<number> = 0;

  unit?: OptionalValue<FieldmagicIntervalUnit> = 'day';

  @Input() standalone: boolean = false;

  @Input() label: string;

  @Input() withRequiredMarker: boolean = false;

  @Input() errors: string[] | string = [];

  @Input() tooltip?: string;

  @Input() withAlways: boolean = false;

  @Input() clearable: boolean = false;

  @Output('change')
  readonly $onChanged = new EventEmitter<FieldmagicIntervalInputValue>();

  readonly units$ = new BehaviorSubject<FieldmagicDropdownOption<string>[]>(AVAILABLE_UNITS);

  readonly disabled$ = new BehaviorSubject<boolean>(false);

  _onChangeFn?: FieldmagicIntervalInputOnChangeHandler;

  _onTouchFn?: FieldmagicIntervalInputOnTouchHandler;

  ngOnInit(): void {
    if (this.withAlways) {
      this.units$.next([
        'always',
        ... this.units$.value,
      ]);
    }
  }

  writeValue(value: FieldmagicIntervalInputValue): void {
    if (blank(value)) {
      this.unit = null;
      this.size = null;

      return;
    }

    if (value != 'always' || ! this.withAlways) {
      const [size, unit] = str_split(value, {
        delimiter: ' ',
      });

      this.size = toNumber(size);
      this.unit = unit as FieldmagicIntervalUnit;
    } else {
      this.size = null;
      this.unit = value;
    }
  }

  registerOnChange = (fn: FieldmagicIntervalInputOnChangeHandler) => this._onChangeFn = fn;

  registerOnTouched = (fn: FieldmagicIntervalInputOnTouchHandler) => this._onTouchFn = fn;

  onInputsChanged(): void {
    const size = fallback(this.size, {
      fallback: () => 1,
    });

    const unit = fallback(this.unit, {
      fallback: () => 'day',
    });

    let value: string;

    if (unit == 'always') {
      value = 'always';
      this.size = null;
    } else {
      value = spf('%d %s', {
        args: [size, unit],
      });
    }

    this._onChanged(value);
  }

  onInputsTouched = () => this._onTouched();

  private _onChanged(value: FieldmagicIntervalInputValue) {
    this.$onChanged.emit(value);

    if (this._onChangeFn) {
      this._onChangeFn(value);
    }
  }

  private _onTouched(): void {
    if (this._onTouchFn) {
      this._onTouchFn();
    }
  }
}

export type FieldmagicIntervalInputValue = OptionalValue<string>;
export type FieldmagicIntervalUnit = 'day' | 'week' | 'month' | 'year' | 'always';
export type FieldmagicIntervalInputOnChangeHandler = (value: FieldmagicIntervalInputValue) => void;
export type FieldmagicIntervalInputOnTouchHandler = () => void;

const AVAILABLE_UNITS = [
  'day',
  'week',
  'month',
  'year',
];